import { useCallback, useState } from 'react'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import { z } from 'zod/v4'
import StripeConnectDashboardButton from '@/components/stripe-connect/StripeConnectDashboardButton'
import StripeConnectAccountManager from '@/components/stripe-connect/StripeConnectAccountManager'
import {
  canUseStripeConnectAccount,
  canUseStripeConnectOnboarding,
} from '@/components/stripe-connect/stripeConnectStatus'
import { STRIPE_CONNECT_ACCOUNT_SECTION } from '@/components/stripe-connect/stripeConnectSurfaceContent'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { stripeHubLink, stripeOnboardingLink } from '@/lib/stripe-financial-routes'
import { assertNotSalesDemoOrganization } from '@/lib/services/sales-demos/sales-demos.guard'
import {
  stripeConnectQueries,
  useStripeConnectStatus,
} from '@/lib/services/stripe-connect/stripe-connect.query'
import { createFileRoute, notFound, redirect, useNavigate, useParams } from '@tanstack/react-router'
import { AlertCircleIcon, Landmark } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/settings/stripe/account-management')({
  component: StripeAccountPage,
  validateSearch: z.object({ account_context: z.enum(['current', 'source']).optional() }),
  loaderDeps: ({ search }) => ({ accountContext: search.account_context ?? 'current' }),
  loader: async ({ context, params, deps }) => {
    if (!context.ability.can('edit', 'stripe-account')) {
      throw notFound()
    }

    // Previous-account bookmarks must never open retained account tools.
    if (deps?.accountContext === 'source') {
      throw redirect({ ...stripeHubLink(params.orgId), search: {}, replace: true })
    }

    await assertNotSalesDemoOrganization(context.queryClient, params.orgId)

    const stripeConnectStatus = await context.queryClient.fetchQuery(
      stripeConnectQueries.status({
        organizationId: params.orgId,
        refresh: true,
      })
    )

    if (canUseStripeConnectOnboarding(stripeConnectStatus?.status)) {
      throw redirect(stripeOnboardingLink(params.orgId))
    }
  },
})

function StripeAccountEmbeddedContent({ orgId, onSessionConflict }) {
  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Landmark}
        title={STRIPE_CONNECT_ACCOUNT_SECTION.title}
        description={STRIPE_CONNECT_ACCOUNT_SECTION.description}
        actions={<StripeConnectDashboardButton />}
      />

      <StripeConnectAccountManager
        key={orgId}
        organizationId={orgId}
        onSessionConflict={onSessionConflict}
        showSectionHeader={false}
      />
    </div>
  )
}

function StripeAccountContent({ orgId }) {
  const navigate = useNavigate()
  const [sessionStatusOverride, setSessionStatusOverride] = useState(null)
  const { data: stripeConnectStatus } = useStripeConnectStatus({
    refresh: true,
    refetchOnMount: false,
  })
  const effectiveStripeStatus = sessionStatusOverride ?? stripeConnectStatus

  const handleSessionConflict = useCallback(
    (nextStatus) => {
      if (canUseStripeConnectOnboarding(nextStatus?.status)) {
        void navigate({
          ...stripeOnboardingLink(orgId),
          replace: true,
        })
        return
      }

      setSessionStatusOverride(nextStatus)
    },
    [navigate, orgId]
  )

  if (effectiveStripeStatus?.retained_account_disconnected) {
    return (
      <InfoNotice tone="warning" title="Your payment account needs attention">
        Contact support to reconnect the existing account. Your previous payments and order history
        remain available.
      </InfoNotice>
    )
  }
  if (canUseStripeConnectAccount(effectiveStripeStatus?.status)) {
    return <StripeAccountEmbeddedContent orgId={orgId} onSessionConflict={handleSessionConflict} />
  }

  return null
}

export function StripeAccountPage() {
  const { orgId } = useParams({ strict: false })

  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <Alert variant="info">
          <AlertCircleIcon />
          <AlertTitle>This is the global org account management page</AlertTitle>
          <AlertDescription>Do not set up a Stripe connect account.</AlertDescription>
        </Alert>
      }
    >
      <StripeAccountContent key={orgId} orgId={orgId} />
    </IfGlobalOrg>
  )
}
